'use client';

import { StatusMessage } from '@/shared/ui/core/status-message';
import Link from 'next/link';
import { HaveNoAccount } from '@/shared/ui/kit/have-no-account';
import { FORGOT_PATHNAME } from '@/shared/constants/pathnames';
import { Separator, Text } from '@/shared/ui/core';
import {
    AuthCard, AuthCardContent, AuthCardDescription, AuthCardFooter, AuthCardHeader, AuthCardLogo, AuthCardTitle,
} from '@/shared/ui/layout/auth-card';
import { LoginForm } from '@/features/auth/login-form';
import { useLoginRedirect } from '../lib/use-login-redirect';
import styles from './login-form-card.module.scss';


type ComponentProps = {
    email?: string;
    emailNotVerified?: boolean;
    emailVerified?: boolean;
    emailVerifiedEarlier?: boolean;
    notFound?: boolean;
    passwordReset?: boolean;
}


export function LoginFormCard ({
    email, emailVerified, emailNotVerified, emailVerifiedEarlier, notFound, passwordReset,
}: ComponentProps) {
    const { redirectTo, inviteEmail, signUpHref } = useLoginRedirect();

    return (
        <AuthCard data-template='login-form-card'>
            <AuthCardLogo data-template='login-form-card-logo'/>

            <AuthCardHeader data-template='login-form-card-header'>
                <AuthCardTitle data-template='login-form-card-title'>
                    Delivering Verified Trust
                </AuthCardTitle>
                <AuthCardDescription data-template='login-form-card-description'>
                    Fill out the form to send a request for carrier verification
                </AuthCardDescription>
            </AuthCardHeader>

            {
                passwordReset &&
                <StatusMessage
                    status='success'
                    className={ styles.status }
                    title={
                        <Text
                            size='xl'
                            tone='success'
                            weight='semibold'
                            lineHeight={ 1 }
                        >
                            Password was changed
                        </Text>
                    }
                >
                    <Text
                        size='lg'
                        tone='success'
                        weight='regular'
                        lineHeight={ 1.2 }
                    >
                        Now you may log in. Welcome
                    </Text>
                </StatusMessage>
            }

            {
                emailVerified &&
                <StatusMessage
                    status='success'
                    className={ styles.status }
                    title={
                        <Text
                            size='xl'
                            tone='success'
                            weight='semibold'
                            lineHeight={ 1 }
                        >
                            Email was verified
                        </Text>
                    }
                >
                    <Text
                        size='lg'
                        tone='success'
                        weight='regular'
                        lineHeight={ 1.2 }
                    >
                        Now you may log in. Welcome
                    </Text>
                </StatusMessage>
            }

            {
                emailVerifiedEarlier &&
                <StatusMessage
                    status='success'
                    className={ styles.status }
                    title={
                        <Text
                            size='xl'
                            tone='success'
                            weight='semibold'
                            lineHeight={ 1 }
                        >
                            Email was verified
                        </Text>
                    }
                >
                    <Text
                        size='lg'
                        tone='success'
                        weight='regular'
                        lineHeight={ 1.2 }
                    >
                        Looks like your email was already verified earlier. Try logging in
                    </Text>
                </StatusMessage>
            }

            {
                emailNotVerified &&
                <StatusMessage
                    status='destructive'
                    className={ styles.status }
                    title={
                        <Text
                            size='xl'
                            tone='destructive'
                            weight='semibold'
                            lineHeight={ 1 }
                        >
                            Email wasn&apos;t verified
                        </Text>
                    }
                >
                    <Text
                        size='lg'
                        tone='destructive'
                        weight='regular'
                        lineHeight={ 1.2 }
                    >
                        Sorry, but your email wasn&apos;t verified. Please, contact our support
                    </Text>
                </StatusMessage>
            }

            {
                notFound &&
                <StatusMessage
                    status='destructive'
                    className={ styles.status }
                    title={
                        <Text
                            size='xl'
                            tone='destructive'
                            weight='semibold'
                            lineHeight={ 1 }
                        >
                            User not found
                        </Text>
                    }
                >
                    <Text
                        size='lg'
                        tone='destructive'
                        weight='regular'
                        lineHeight={ 1.2 }
                    >
                        Sorry, but user associated with passed verification token wasn&apos;t found. Try again or contact our support
                    </Text>
                </StatusMessage>
            }

            <AuthCardContent data-template='login-form-card-content'>

                <LoginForm
                    email={ email ?? inviteEmail ?? undefined }
                    redirectTo={ redirectTo }
                    ForgotPassword={
                        <Link href={ FORGOT_PATHNAME }>
                            <Text
                                size='lg'
                                tone='secondary'
                                className={ styles.forgot }
                            >
                                Forgot your password?
                            </Text>
                        </Link>
                    }
                />

            </AuthCardContent>

            <AuthCardFooter data-template='login-form-card-footer'>
                <Separator/>

                <HaveNoAccount
                    href={ signUpHref }
                    className={ styles.footer_description }
                />
            </AuthCardFooter>
        </AuthCard>
    );
}
